import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCart as fetchCart, addToCart as apiAddToCart, updateCart as apiUpdateCart, removeFromCart as apiRemoveFromCart, clearCart as apiClearCart } from './api';
import { useAuth } from './auth';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Close cart on route change
  useEffect(() => { setCartOpen(false); }, [location.pathname]);

  const refreshCart = useCallback(async () => {
    if (!user) { setCart({ items: [], total: 0 }); return; }
    try {
      setLoading(true);
      const res = await fetchCart();
      setCart(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);

  useEffect(() => { refreshCart(); }, [refreshCart]);

  const addToCart = async (productId, quantity = 1) => {
    await apiAddToCart({ product_id: productId, quantity });
    await refreshCart();
    setCartOpen(true);
  };

  const updateItem = async (productId, quantity) => {
    await apiUpdateCart({ product_id: productId, quantity });
    await refreshCart();
  };

  const removeItem = async (productId) => {
    await apiRemoveFromCart(productId);
    await refreshCart();
  };

  const clearAll = async () => {
    await apiClearCart();
    await refreshCart();
  };

  const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, cartOpen, setCartOpen, addToCart, updateItem, removeItem, clearAll, itemCount, loading, refreshCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
